import type { ComponentProps } from "react";
import { Switch as SwitchPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";

import { AVAILABLE_LANGUAGE } from "@/shared/models";
import { useSettingsStore } from "@/shared/stores";

const styles = tv({
  slots: {
    root: "relative inline-flex h-fit shrink-0 cursor-pointer items-center overflow-hidden rounded-lg border border-neutral-300 bg-white text-neutral-950 shadow-xs transition-all focus-visible:ring-2 focus-visible:ring-secondary-200 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-200 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:*:opacity-50 data-[state=checked]:[&>div>*:first-child]:text-neutral-700 data-[state=unchecked]:[&>div>*:last-child]:text-neutral-700",
    labels: "z-10 flex gap-6 px-3 py-2.5 font-medium",
    label: "transition-colors duration-500",
    thumb:
      "pointer-events-none absolute block h-full w-1/2 translate-x-full rounded-lg border-neutral-300 bg-primary-50 ring-0 transition-all duration-500 data-[state=checked]:right-11 data-[state=checked]:border-l data-[state=unchecked]:right-full data-[state=unchecked]:border-r",
    button:
      "flex size-9 items-center justify-center rounded-lg border border-neutral-300 bg-primary-50 text-sm font-medium text-neutral-950",
  },
});

const { root, label, labels, thumb, button } = styles();

type LanguageSwitchProps = Omit<
  ComponentProps<typeof SwitchPrimitive.Root>,
  "checked" | "leftLabel" | "rightLabel"
>;

export const LanguageSwitch = ({
  onCheckedChange,
  className,
  ...props
}: LanguageSwitchProps) => {
  const localeCode = useSettingsStore((state) => state.language);
  const setLanguage = useSettingsStore((state) => state.setLanguage);

  const handleOnCheckedChange = (checked: boolean) => {
    setLanguage(checked ? AVAILABLE_LANGUAGE.ES : AVAILABLE_LANGUAGE.EN);
    onCheckedChange?.(checked);
  };

  return (
    <SwitchPrimitive.Root
      {...props}
      className={root({ className })}
      checked={localeCode === AVAILABLE_LANGUAGE.ES}
      onCheckedChange={handleOnCheckedChange}
    >
      <div className={labels()}>
        <span className={label()}>{AVAILABLE_LANGUAGE.EN.toUpperCase()}</span>
        <span className={label()}>{AVAILABLE_LANGUAGE.ES.toUpperCase()}</span>
      </div>

      <SwitchPrimitive.Thumb className={thumb()} />
    </SwitchPrimitive.Root>
  );
};

export const LanguageButton = ({
  className,
  ...props
}: ComponentProps<"button">) => {
  const localeCode = useSettingsStore((state) => state.language);
  const setLanguage = useSettingsStore((state) => state.setLanguage);

  return (
    <button
      type="button"
      className={button({ className })}
      onClick={() =>
        setLanguage(
          localeCode === AVAILABLE_LANGUAGE.ES
            ? AVAILABLE_LANGUAGE.EN
            : AVAILABLE_LANGUAGE.ES,
        )
      }
      {...props}
    >
      {localeCode.toUpperCase()}
    </button>
  );
};
